import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import Form from "../layout/Form";
import FormFieldPassword from "../input/FormFieldPassword";
import fetchWrapper from "../../lib/apiCall";
import { errorToast, successfulToast } from "../../util/toastNotifications";
import { isValidPassword } from "../../util/stringUtils";

const INITIAL_PASSWORDS = {
  current: "",
  new: "",
  verify: "",
};

const ChangePasswordModal = ({
  userId,
  requireCurrentPassword,
  onClose,
}) => {
  const [passwords, setPasswords] = useState(INITIAL_PASSWORDS);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const errors = {};

    if (requireCurrentPassword && !passwords.current.trim()) {
      errors.current = "Current password is required";
    }

    if (!isValidPassword(passwords.new)) {
      errors.new =
        "Password must be between 8-20 characters with one special character, one number, and one capital letter.";
    }

    if (passwords.new !== passwords.verify) {
      errors.verify = "Passwords do not match";
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  const handleFieldUpdate = (e) => {
    const { name, value } = e.target;

    setPasswords((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formValidation = validateForm();
    if (formValidation) {
      setFormErrors(formValidation);
      return;
    }

    setIsSubmitting(true);

    try {
      if (requireCurrentPassword) {
        await fetchWrapper.apiCall("/auth/change-password", "PATCH", {
          current_password: passwords.current,
          new_password: passwords.new,
          verify_password: passwords.verify,
        });
      } else {
        await fetchWrapper.apiCall(`/user/${userId}`, "PUT", {
          password: passwords.new,
        });
      }

      successfulToast("Password updated successfully");
      onClose();
    } catch (err) {
      let message = "Unable to update password. Please try again.";

      if (err.status === 401) {
        message = "Current password is incorrect";
      } else if (err.message) {
        message = err.message;
      }

      errorToast(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="change-password-modal">
      <h2>Change Password</h2>

      <Form onSubmit={handleSubmit}>
        {requireCurrentPassword && (
          <FormFieldPassword
            required
            name="current"
            label="Current Password *"
            placeholder="Enter your current password..."
            value={passwords.current}
            onChange={handleFieldUpdate}
            errorMessage={formErrors.current}
          />
        )}

        <FormFieldPassword
          required
          name="new"
          label="New Password *"
          placeholder="Enter your new password..."
          value={passwords.new}
          onChange={handleFieldUpdate}
          errorMessage={formErrors.new}
        />

        <FormFieldPassword
          required
          name="verify"
          label="Verify New Password *"
          placeholder="Re-enter your new password..."
          value={passwords.verify}
          onChange={handleFieldUpdate}
          errorMessage={formErrors.verify}
        />

        <div className="button-row">
          <div className="button-row-left">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>

          <div className="button-row-right">
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default ChangePasswordModal;
